import * as React from 'react';
import { ActionGroup, Alert, Button, Checkbox, Form, FormAlert, FormGroup, FormHelperText, Modal, ModalVariant, TextInput, Title } from '@patternfly/react-core';
import { ExclamationCircleIcon } from '@patternfly/react-icons';
import ValidatedType from '@app/Type/ValidatedType';
import TypeAssertions from '@app/utils/TypeAssertions';
import FormAlertInterface from '@app/Interface/FormAlertInterface';
import TwoFactorHelper from '@app/Service/TwoFactorHelper';
import ItemLoader from '../ItemLoader';

interface UserVerifyAuthFactorModalProps {
    typeToVerify: string;
    deliveryMethodToVerify: string;
    registeringUserId?: number;
    registeringUserJWT?: string;
    isModalOpen: boolean;
    handleFactorValidated: () => void;
    handleCloseClicked: () => void;
}

const UserVerifyAuthFactorModal: React.FC<UserVerifyAuthFactorModalProps> = ({
    typeToVerify,
    deliveryMethodToVerify,
    registeringUserId,
    registeringUserJWT,
    isModalOpen,
    handleFactorValidated,
    handleCloseClicked
}) => {
    const isRegistrationVerification = !!registeringUserId && !!registeringUserJWT;

    const [twoFactorCode, setTwoFactorCode] = React.useState("");

    const [sendingCode, setSendingCode] = React.useState(false);

    const [validCode, setValidCode] = React.useState<ValidatedType>("default");

    const [isFormProcessing, setIsFormProcessing] = React.useState(false);

    const [formValidated, setFormValidated] = React.useState<ValidatedType>("default");

    const [formAlertMessage, setFormAlertMessage] = React.useState<FormAlertInterface>({ variant: "default", title: "", isInline: true });

    const [codeValidated, setCodeValidated] = React.useState(false);

    const [completingVerification, setCompletingVerification] = React.useState(false);

    const [disableResendButton, setDisableResendButton] = React.useState(false);

    const handleSubmit = async (event: React.SyntheticEvent) => {
        event.preventDefault();

        setIsFormProcessing(true);
        setDisableResendButton(true);
        setValidCode("default");
        setFormValidated("default");

        if (TypeAssertions.isStringNullOrEmpty(twoFactorCode)) {
            setValidCode("error");
        } else {
            // How we verify the 2FA code depends on if the component was rendered during registration or not
            const [newCodeValidated, errorMessage] = isRegistrationVerification
                ? await TwoFactorHelper.RegistrationVerifyCode(twoFactorCode, registeringUserId, registeringUserJWT, typeToVerify, deliveryMethodToVerify)
                : await TwoFactorHelper.verifyCode(twoFactorCode, typeToVerify, deliveryMethodToVerify);

            if (newCodeValidated) {
                setCodeValidated(true);
            } else {
                setFormValidated("error");
                setFormAlertMessage({ variant: "danger", title: errorMessage, isInline: true });
            }
        }

        setIsFormProcessing(false);
        setDisableResendButton(false);
    };

    const handleResendCode = async () => {
        const controller = new AbortController();

        let mounted = true;

        async function sendCode() {
            // How we send the 2FA code depends on if the component was rendered during registration or not
            const [sent, errorMessage] = isRegistrationVerification
                ? await TwoFactorHelper.registrationSendTwoFactorCode(controller, registeringUserId, registeringUserJWT, typeToVerify, deliveryMethodToVerify)
                : await TwoFactorHelper.sendTwoFactorCode(controller, typeToVerify, deliveryMethodToVerify);

            if (mounted) {
                setTwoFactorCode("");

                if (sent) {
                    setFormValidated("success");
                    setFormAlertMessage({ variant: "success", title: "A code was successfully sent.", isInline: true });
                } else {
                    setFormValidated("error");
                    setFormAlertMessage({ variant: "danger", title: errorMessage, isInline: true });
                }

                setSendingCode(false);
            }

        }

        setSendingCode(true);

        sendCode();

        return () => {
            mounted = false;
            controller?.abort();
        };
    }

    // on modal open, send code to user
    React.useEffect(() => {
        const controller = new AbortController();

        let mounted = true;

        async function sendCode() {
            // send the code
            const [sent, errorMessage] = isRegistrationVerification
                ? await TwoFactorHelper.registrationSendTwoFactorCode(controller, registeringUserId, registeringUserJWT, typeToVerify, deliveryMethodToVerify)
                : await TwoFactorHelper.sendTwoFactorCode(controller, typeToVerify, deliveryMethodToVerify);

            if (mounted) {
                if (sent) {
                    setFormValidated("success");
                    setFormAlertMessage({ variant: "success", title: "A code was successfully sent.", isInline: true });
                } else {
                    setFormValidated("error");
                    setFormAlertMessage({ variant: "danger", title: errorMessage, isInline: true });
                }

                setSendingCode(false);
            }
        }

        if (isModalOpen) {
            setSendingCode(true);
            sendCode();
        }

        return () => {
            mounted = false;
            controller?.abort();
        };

    }, [isModalOpen]);

    // If validated code, complete the verification
    React.useEffect(() => {

        const controller = new AbortController();

        let mounted = true;

        async function completeVerification() {
            if (mounted) {
                if (handleFactorValidated) {
                    handleFactorValidated();
                } else {
                    console.warn("no handleLogin function was passed!");
                }

                setCompletingVerification(false);
                // Reset codeValidated in case the user needs to validate another factor
                setCodeValidated(false);
            }

        }

        if (codeValidated) {
            setCompletingVerification(true);
            setTwoFactorCode('');
            completeVerification();
        }

        return () => {
            mounted = false;
            controller?.abort();
        };

    }, [codeValidated]);

    const getModalContent = () => {
        if (completingVerification) {
            return (
                <>
                    <Title headingLevel="h2">Verification completed, popup will close automatically</Title>
                    <ItemLoader />
                </>
            );
        }

        if (sendingCode) {
            return (
                <>
                    <Title headingLevel="h2">Sending code</Title>
                    <ItemLoader />
                </>
            );
        }

        return (
            <Form autoComplete="false" onSubmit={handleSubmit}>
                {formValidated !== "default" && (
                    <FormAlert>
                        <Alert
                            variant={formAlertMessage.variant}
                            title={formAlertMessage.title}
                            aria-live="polite"
                            isInline={formAlertMessage.isInline}
                            role="alert"
                        ></Alert>
                    </FormAlert>
                )}
                <FormGroup
                    label="Enter Your Code"
                    type="string"
                    helperText={
                        // eslint-disable-next-line react/jsx-no-undef
                        <FormHelperText icon={<ExclamationCircleIcon />} isHidden={validCode !== "error"}>Please enter a code.</FormHelperText>
                    }
                    helperTextInvalid="Enter the code"
                    helperTextInvalidIcon={<ExclamationCircleIcon />}
                    fieldId="twoFactorCode"
                    validated={validCode}
                >
                    <TextInput
                        validated={validCode}
                        value={twoFactorCode}
                        id="twoFactorCode"
                        aria-describedby="twoFactorCode"
                        onChange={(newValue: string) => setTwoFactorCode(newValue)}
                        autoComplete="one-time-code"
                        inputMode="numeric"
                    />
                </FormGroup>
                <ActionGroup>
                    <Button variant="primary" onClick={handleSubmit} isLoading={isFormProcessing}>
                        {isFormProcessing ? "Sending" : "Submit"}
                    </Button>
                    <Button variant="link" onClick={handleResendCode} disabled={disableResendButton}>
                        {sendingCode ? "Sending" : "Resend Code"}
                    </Button>

                </ActionGroup>
            </Form>
        );
    };

    return (
        <Modal
            variant={ModalVariant.medium}
            isOpen={isModalOpen}
            onClose={handleCloseClicked}
            title="Verifying Your New 2FA Settings"
            description="We'll send a 2FA code to the new email or phone number your provided. Please enter the code below before proceeding."
        >
            {getModalContent()}
        </Modal>
    );

}

export default UserVerifyAuthFactorModal;
